import { useEffect, useRef, useState } from "react";

/**
 * The Institute's attendance certificates — four approved designs, drawn here
 * rather than shipped as flat artwork.
 *
 * The designs arrived as finished images with the event name set into them:
 * "NATIONAL WORKSHOP", "MANDATORY REFRESHER COURSE (MRC)" and so on. That is
 * exactly the part that has to change per event, so the artwork is rebuilt in
 * CSS and SVG and the title, theme, year and recipient are live text. Nothing
 * on the sheet is an image except the Institute's own lockup, which is the one
 * element that must never be redrawn by hand.
 *
 * One definition, printed and previewed: `certificateDocument()` builds the
 * whole page, `printCertificate()` opens it in a print window, and
 * `CertificatePreview` shows the SAME html in a scaled iframe. The desk, the
 * member and the admin choosing a format on the event form are therefore all
 * looking at the sheet that will actually come out of the printer.
 *
 * Sized in millimetres against A4 landscape throughout, so what the browser
 * lays out is what the paper gets — a certificate laid out in pixels and
 * scaled at print time is a certificate whose margins move per printer.
 */

export type CertificateTemplateKey =
  | "national_workshop"
  | "refresher_course"
  | "biennial_conference"
  | "annual_conference";

/** One signature block: the office, and optionally who holds it. */
export type CertificateSignature = {
  /** Printed under the rule in the prominent face. Optional. */
  name?: string | null;
  /** The office. Printed under the name, or alone in its place. */
  title?: string | null;
  /** Scanned signature, printed above the rule. Optional. */
  signature_url?: string | null;
};

/** The API's certificate payload (GET /my/events/{id}/certificate, or the admin twin). */
export type CertificateData = {
  template?: CertificateTemplateKey | string | null;
  template_name?: string | null;
  /** Printed left to right; its length is how many blocks the foot draws. */
  signatures?: CertificateSignature[] | null;
  certificate_no?: string | null;
  recipient_name?: string | null;
  membership_no?: string | null;
  registration_no?: string | null;
  event_title?: string | null;
  event_theme?: string | null;
  event_year?: number | string | null;
  event_location?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  cpd_points?: number | string | null;
  issued_on?: string | null;
};

// ── Text helpers ─────────────────────────────────────────────────────────────

// Escapes into the print window's HTML. Event titles, themes and venues are
// free text typed by admins, and names are typed by members; without this an
// ampersand in "Biennial Conference & General Meeting" or an apostrophe in a
// surname silently breaks the sheet it is printed on.
const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * The title as it prints: upper case, with the year appended unless the title
 * already carries it. "2026 Annual Conference" must not become "2026 ANNUAL
 * CONFERENCE 2026".
 */
function headlineTitle(c: CertificateData): string {
  const title = (c.event_title ?? "").trim();
  const year = c.event_year != null && c.event_year !== "" ? String(c.event_year) : "";

  if (!year || title.includes(year)) return title.toUpperCase();
  return `${title} ${year}`.toUpperCase();
}

// ── Wording ──────────────────────────────────────────────────────────────────

/**
 * The fixed wording around the name, which is NOT the same on all three.
 *
 * Taken line for line off the approved artwork: the workshop sheet carries a
 * colon and names the Institute on a second line, the refresher sheet does
 * neither, and the two conference sheets set both lines in sentence case
 * rather than caps. Held as literal text with its own casing rather than
 * upper-cased in CSS, so a conference sheet cannot be shouted at by a
 * stylesheet rule written for the other two.
 */
const COPY: Record<string, { certify: string; lead: string[] }> = {
  national_workshop: {
    certify: "THIS IS TO CERTIFY THAT:",
    lead: ["ATTENDED & FULLY PARTICIPATED IN THE", "NIGERIAN INSTITUTE OF QUANTITY SURVEYORS (NIQS)"],
  },
  refresher_course: {
    certify: "THIS IS TO CERTIFY THAT",
    lead: ["ATTENDED AND FULLY PARTICIPATED IN THE"],
  },
  biennial_conference: {
    certify: "This is to certify that",
    lead: ["Attended and fully participated in the"],
  },
  annual_conference: {
    certify: "This is to certify that",
    lead: ["Attended and fully participated in the"],
  },
};

/**
 * Where the seal sits at the foot, per design.
 *
 * The only thing that separates the biennial sheet from the annual one: the
 * same cream ribbon artwork, with the seal pushed to the left to leave the
 * middle for a third signature. Three or more blocks force it left whichever
 * design is chosen, because the middle is no longer free.
 */
const SEAL_LEFT: Record<string, boolean> = { biennial_conference: true };

// ── Seal ─────────────────────────────────────────────────────────────────────

/**
 * The Institute's red seal, as a starburst disc.
 *
 * Built from a generated path rather than a wall of coordinates so the tooth
 * count can be changed in one number. Drawn rather than cropped out of the
 * supplied artwork because the sheets it sits on are blue, white and cream —
 * a cropped seal would carry its own background onto three of the four.
 */
function sealSvg(): string {
  const teeth = 44;
  const outer = 48;
  const inner = 43;
  const pts: string[] = [];

  for (let i = 0; i < teeth * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / teeth - Math.PI / 2;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`);
  }

  return `<svg class="seal" viewBox="0 0 100 100" aria-hidden="true">
  <polygon points="${pts.join(" ")}" fill="#d2232a"/>
  <circle cx="50" cy="50" r="40" fill="#b8171f"/>
  <circle cx="50" cy="50" r="34" fill="none" stroke="#f0a9ac" stroke-width="0.9" opacity=".65"/>
  <circle cx="50" cy="50" r="31" fill="#8e1016"/>
  <g fill="#f3c9cb" font-family="Georgia,'Times New Roman',serif" font-size="9.5" font-weight="700" text-anchor="middle">
    <text x="50" y="34">N</text>
    <text x="50" y="45">I</text>
    <text x="50" y="56">Q</text>
    <text x="50" y="67">S</text>
  </g>
  <circle cx="50" cy="50" r="31" fill="none" stroke="#f0a9ac" stroke-width="0.7" opacity=".5"/>
</svg>`;
}

// ── Template chrome ──────────────────────────────────────────────────────────

/**
 * The decorative layers behind the text, per design. Everything here is
 * absolutely positioned inside the sheet and sits under `.content`.
 */
function chrome(template: string): string {
  switch (template) {
    // Pale blue over construction line-work, gold and navy corner rules.
    case "national_workshop":
      return `
  <div class="bp-lines"></div>
  <svg class="bp-draw" viewBox="0 0 297 210" preserveAspectRatio="none" aria-hidden="true">
    <g stroke="#9fb4e2" stroke-width="0.3" fill="none" opacity=".55">
      <path d="M8 210 L8 96 L52 74 L52 210 Z"/>
      <path d="M52 74 L96 92 L96 210 L52 210 Z"/>
      <path d="M96 92 L140 66 L140 210 L96 210 Z"/>
      <path d="M236 210 L236 58 L278 42 L278 210 Z"/>
      <path d="M190 210 L190 110 L236 92"/>
      ${Array.from({ length: 11 }, (_, i) => `<path d="M8 ${100 + i * 10} L140 ${100 + i * 10}"/>`).join("")}
      ${Array.from({ length: 8 }, (_, i) => `<path d="M236 ${66 + i * 18} L278 ${56 + i * 18}"/>`).join("")}
      ${Array.from({ length: 6 }, (_, i) => `<path d="M${20 + i * 20} 96 L${20 + i * 20} 210"/>`).join("")}
    </g>
  </svg>
  ${["tl", "tr", "bl", "br"].map((k) => `<div class="corner gold ${k}"></div><div class="corner navy ${k}"></div>`).join("")}`;

    // White, framed by navy and gold chevrons down both edges.
    case "refresher_course":
      return `
  <div class="cv navy left"></div>
  <div class="cv gold left"></div>
  <div class="cv navy right"></div>
  <div class="cv gold right"></div>
  <svg class="cv-waves" viewBox="0 0 297 210" preserveAspectRatio="none" aria-hidden="true">
    <g stroke="#d9bd7e" stroke-width="0.25" fill="none" opacity=".5">
      ${Array.from({ length: 9 }, (_, i) => `<path d="M40 ${150 + i * 6} Q 148 ${118 + i * 6} 257 ${150 + i * 6}"/>`).join("")}
    </g>
  </svg>
  <div class="cv-frame"></div>`;

    // Cream, gold border, navy corner swoop and a navy ribbon banner.
    case "biennial_conference":
    case "annual_conference":
      return `
  <div class="rb-hatch"></div>
  <div class="rb-border"></div>
  <div class="rb-swoop"></div>
  <div class="rb-inner"></div>`;

    default:
      return "";
  }
}

/** The heading block, which is where the four designs differ most. */
function heading(template: string): string {
  switch (template) {
    case "refresher_course":
      return `
    <div class="hd-chevron">
      <div class="hd-main">CERTIFICATE</div>
      <div class="hd-rule"></div>
      <div class="hd-sub">OF ATTENDANCE</div>
    </div>`;

    case "biennial_conference":
    case "annual_conference":
      return `
    <div class="hd-ribbon"><span class="banner">Certificate of Attendance</span></div>`;

    case "national_workshop":
    default:
      return `<div class="hd-script">Certificate of Attendance</div>`;
  }
}

// ── Stylesheet ───────────────────────────────────────────────────────────────

const STYLES = `
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4 landscape;margin:0}
  html,body{width:297mm;height:210mm}
  body{background:#e5e7eb;display:flex;align-items:center;justify-content:center;
       font-family:'Poppins','Segoe UI',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}

  /* No z-index, no isolation, no opacity: a stacking context here would cut
     the signature blend off from the sheet's own background. */
  .sheet{position:relative;width:297mm;height:210mm;overflow:hidden;background:#fff;flex:none}

  /* ── Content, common to all four ─────────────────────────────────────── */
  .content{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;
           padding:17mm 30mm 13mm;text-align:center}
  .lockup{height:26mm;object-fit:contain;flex:none}
  .mark{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
         width:100%;gap:0;padding-top:14mm}

  /* No text-transform: COPY carries each design's own casing. */
  .certify{font-size:3.6mm;font-weight:500;letter-spacing:.06em;color:#1d1d1f;margin-top:1mm}

  .name-line{width:100%;margin:7mm 0 6mm;padding-bottom:1.6mm}
  .name{display:block;font-size:9mm;line-height:1.1;font-weight:600;color:#101c74;
        font-family:'Cormorant Garamond',Georgia,serif;min-height:10mm}
  .name-line.dotted{border-bottom:0.7mm dotted #1b2a5e}
  .name-line.solid{border-bottom:0.35mm solid #1d1d1f}

  .lead{font-size:3.7mm;line-height:1.65;font-weight:500;color:#1d1d1f;letter-spacing:.02em}
  .title{font-size:11mm;line-height:1.08;font-weight:800;color:#1a1aa8;letter-spacing:-.01em;margin-top:3mm;
         text-transform:uppercase;max-width:100%;overflow-wrap:break-word}
  /* Long names of events wrap rather than overflow; the two-line case is why
     the block above it is a flexible middle and not a fixed offset. */
  .title.long{font-size:9.2mm}
  .title.xlong{font-size:7mm}
  .theme{font-size:4.2mm;line-height:1.45;font-style:italic;color:#3a3a46;margin-top:3mm;max-width:215mm}
  .theme{margin-bottom:3mm}

  /* ── Foot: seal and signatures ───────────────────────────────────────── */
  .footer{width:100%;flex:none}
  /* The gap is what the seal sits in. Two blocks are pushed apart to clear
     it; three fill the middle, so the seal moves to the left instead. */
  .foot{position:relative;width:100%;display:flex;align-items:flex-end;justify-content:center;gap:30mm}
  /* Wide enough for "Professional Development & Library" on one line — the
     longest office any of the approved sheets carries — and the gap beside it
     wide enough for the seal to sit between two blocks without touching. */
  .sig{flex:0 1 75mm;min-width:0;display:flex;flex-direction:column;justify-content:flex-end}
  .sig-body{border-top:0.4mm solid #1b2a5e;padding-top:1.8mm}
  /* The scanned signature, sitting ON the rule the way a signed one does.
     Only reserved when some block on this sheet actually has one — otherwise
     every unsigned certificate would carry a band of blank space. */
  .foot.inked .sig-ink{height:14mm;display:flex;align-items:flex-end;justify-content:center;
                       padding-bottom:0.8mm;overflow:hidden}
  .foot.inked .sig-ink img{max-height:100%;max-width:85%;object-fit:contain;
                           /* Scans arrive as white-background JPEGs as often
                              as cut-out PNGs; multiply drops the white. */
                           mix-blend-mode:multiply}
  .sig-name{font-size:3.3mm;font-weight:700;color:#101c74;line-height:1.25}
  .sig-title{font-size:2.8mm;font-weight:600;color:#5b6072;letter-spacing:.02em;line-height:1.3;margin-top:0.6mm}
  /* With no name the office takes the prominent face — which is the approved
     artwork exactly, where the blocks carry an office and nothing else. */
  .sig.no-name .sig-title{font-size:3.9mm;font-weight:600;color:#14216b;margin-top:0}
  .seal{position:absolute;bottom:3mm;left:50%;transform:translateX(-50%);width:24mm;height:24mm;z-index:1}
  /* Foot-left on the biennial sheet, and on any sheet whose third signature
     block has taken the middle. */
  /* The blocks shift right to clear it — otherwise the first rule runs
     under the seal, whether there are two of them or three. */
  .foot.seal-left{padding-left:25mm}
  .foot.seal-left .seal{left:0;transform:none}
  .foot.cols-3,.foot.cols-4{justify-content:space-between}
  .foot.cols-3{gap:10mm}
  .foot.cols-3 .sig{flex:0 1 60mm}
  .foot.cols-3 .sig-name{font-size:3mm}
  .foot.cols-3 .sig-title{font-size:2.6mm}
  .foot.cols-3 .sig.no-name .sig-title{font-size:3.4mm}
  .foot.cols-4{gap:7mm}
  .foot.cols-4 .sig{flex:0 1 42mm}
  .foot.cols-4 .sig-name{font-size:2.7mm}
  .foot.cols-4 .sig-title{font-size:2.4mm}
  .foot.cols-4 .sig.no-name .sig-title{font-size:3mm}

  /* In the flow under the signatures rather than pinned to the sheet corner:
     every one of the four designs has a frame, a border or a corner rule
     sitting in that corner. */
  .serial{margin-top:3.5mm;text-align:center;font-size:2.5mm;font-weight:700;
          letter-spacing:.14em;color:#9aa0b4}

  /* The faint house mark, right of centre, as on the approved artwork.
     The only logo file is the horizontal lockup, so the window crops it to
     the eagle and roundel at its left — blowing up the whole lockup would
     lay the words NIGERIAN INSTITUTE OF QUANTITY SURVEYORS across the middle
     of the sheet, over the recipient's own name. */
  .watermark{position:absolute;top:50%;left:63%;transform:translate(-50%,-48%);
             width:68mm;height:84mm;overflow:hidden;opacity:.06;pointer-events:none}
  .watermark img{position:absolute;top:50%;left:0;transform:translateY(-50%);width:316mm;max-width:none}

  /* ══ Blueprint ═══════════════════════════════════════════════════════ */
  .tpl-national_workshop{background:#e9effc}
  .tpl-national_workshop .bp-lines{position:absolute;inset:0;
    background:repeating-linear-gradient(90deg,rgba(150,173,219,.18) 0 0.25mm,transparent 0.25mm 9mm),
               repeating-linear-gradient(0deg,rgba(150,173,219,.14) 0 0.25mm,transparent 0.25mm 9mm)}
  .tpl-national_workshop .bp-draw{position:absolute;inset:0;width:100%;height:100%}
  .tpl-national_workshop .watermark{opacity:.085}
  .tpl-national_workshop .corner{position:absolute}
  .tpl-national_workshop .corner.gold{width:34mm;height:28mm;border-color:#cfa84e;border-style:solid;border-width:0}
  .tpl-national_workshop .corner.navy{width:26mm;height:21mm;border-color:#101c74;border-style:solid;border-width:0}
  .tpl-national_workshop .corner.gold.tl{top:9mm;left:9mm;border-top-width:2.2mm;border-left-width:2.2mm}
  .tpl-national_workshop .corner.gold.tr{top:9mm;right:9mm;border-top-width:2.2mm;border-right-width:2.2mm}
  .tpl-national_workshop .corner.gold.bl{bottom:9mm;left:9mm;border-bottom-width:2.2mm;border-left-width:2.2mm}
  .tpl-national_workshop .corner.gold.br{bottom:9mm;right:9mm;border-bottom-width:2.2mm;border-right-width:2.2mm}
  .tpl-national_workshop .corner.navy.tl{top:13mm;left:13mm;border-top-width:0.8mm;border-left-width:0.8mm}
  .tpl-national_workshop .corner.navy.tr{top:13mm;right:13mm;border-top-width:0.8mm;border-right-width:0.8mm}
  .tpl-national_workshop .corner.navy.bl{bottom:13mm;left:13mm;border-bottom-width:0.8mm;border-left-width:0.8mm}
  .tpl-national_workshop .corner.navy.br{bottom:13mm;right:13mm;border-bottom-width:0.8mm;border-right-width:0.8mm}
  .hd-script{font-family:'Pinyon Script','Snell Roundhand','Apple Chancery',cursive;
             font-size:19mm;line-height:1.15;color:#0d0d12;margin-top:4mm;padding:0 4mm}

  /* ══ Chevron ═════════════════════════════════════════════════════════ */
  .tpl-refresher_course{background:#fff}
  .tpl-refresher_course .cv{position:absolute;top:0;height:100%;width:40mm}
  .tpl-refresher_course .cv.navy{background:#101c74}
  .tpl-refresher_course .cv.gold{background:#cfa84e}
  .tpl-refresher_course .cv.left{left:0}
  .tpl-refresher_course .cv.right{right:0}
  .tpl-refresher_course .cv.navy.left{clip-path:polygon(0 0,16mm 0,40mm 105mm,16mm 210mm,0 210mm,24mm 105mm)}
  .tpl-refresher_course .cv.gold.left{clip-path:polygon(0 0,7mm 0,31mm 105mm,7mm 210mm,0 210mm,15mm 105mm);opacity:.95}
  .tpl-refresher_course .cv.navy.right{clip-path:polygon(40mm 0,24mm 0,0 105mm,24mm 210mm,40mm 210mm,16mm 105mm)}
  .tpl-refresher_course .cv.gold.right{clip-path:polygon(40mm 0,33mm 0,9mm 105mm,33mm 210mm,40mm 210mm,25mm 105mm);opacity:.95}
  .tpl-refresher_course .cv-frame{position:absolute;inset:7mm;border:1.1mm solid #cfa84e}
  .tpl-refresher_course .cv-frame::after{content:"";position:absolute;inset:1.4mm;border:0.3mm solid #cfa84e}
  .tpl-refresher_course .cv-waves{position:absolute;inset:0;width:100%;height:100%}
  .tpl-refresher_course .content{padding-left:46mm;padding-right:46mm}
  .tpl-refresher_course .title.long{font-size:8mm}
  .tpl-refresher_course .title.xlong{font-size:6.4mm}
  .hd-chevron{margin-top:5mm}
  .hd-chevron .hd-main{font-family:'Cormorant Garamond',Georgia,serif;font-size:15mm;line-height:1;
                       font-weight:400;letter-spacing:.17em;color:#101c74;text-indent:.17em}
  .hd-chevron .hd-rule{width:86mm;height:0.3mm;background:#cfa84e;margin:3.5mm auto 3mm}
  .hd-chevron .hd-sub{font-family:'Cormorant Garamond',Georgia,serif;font-size:5.4mm;
                      font-weight:600;letter-spacing:.42em;color:#1d1d1f;text-indent:.42em}

  /* ══ Ribbon (and its three-signature twin) ═══════════════════════════ */
  .tpl-biennial_conference,.tpl-annual_conference{background:#fdf8e9}
  .rb-hatch{position:absolute;inset:0;
    background:repeating-linear-gradient(45deg,rgba(207,168,78,.16) 0 0.3mm,transparent 0.3mm 2.2mm)}
  .rb-border{position:absolute;inset:0;border:9mm solid #e7c475}
  .rb-border::after{content:"";position:absolute;inset:-9mm;border:1.6mm solid #cfa84e}
  /* The navy quarter-round that cuts across the top-left corner, over the
     gold border, with a gold arc following its edge. */
  .rb-swoop{position:absolute;top:0;left:0;width:62mm;height:48mm;background:#101c74;
            border-bottom-right-radius:62mm 48mm}
  .rb-swoop::after{content:"";position:absolute;inset:0;border-bottom-right-radius:62mm 48mm;
                   border-right:0.8mm solid #e7c475;border-bottom:0.8mm solid #e7c475}
  .rb-inner{position:absolute;inset:13mm;border:0.9mm solid #101c74}
  .rb-inner::after{content:"";position:absolute;inset:1.1mm;border:0.3mm solid #101c74}
  .tpl-biennial_conference .content,.tpl-annual_conference .content{padding:19mm 34mm 16mm}
  .hd-ribbon{position:relative;display:flex;justify-content:center;margin-top:4mm}
  .hd-ribbon .banner{display:flex;align-items:center;justify-content:center;background:#101c74;color:#fff;
                     font-family:'UnifrakturMaguntia','Old English Text MT',Georgia,serif;
                     font-size:15.5mm;line-height:1;padding:1.6mm 12mm 2.6mm;
                     clip-path:polygon(0 0,100% 0,calc(100% - 8mm) 100%,8mm 100%)}
  .tpl-biennial_conference .title,.tpl-annual_conference .title{color:#1a1aa8}
  /* The conference sheets set both lines larger and lighter than the other two. */
  .tpl-biennial_conference .certify,.tpl-annual_conference .certify{font-size:5.4mm;font-weight:400;letter-spacing:0}
  .tpl-biennial_conference .lead,.tpl-annual_conference .lead{font-size:5mm;font-weight:400;letter-spacing:0}
`;

// ── Document ─────────────────────────────────────────────────────────────────

/**
 * The whole printable page for one certificate.
 *
 * `origin` is where the Institute lockup is fetched from. It is passed in
 * rather than read here so the preview iframe and the print window, which have
 * different documents but the same host, resolve it identically.
 */
export function certificateDocument(c: CertificateData, opts?: { origin?: string; autoPrint?: boolean }): string {
  const origin = opts?.origin ?? window.location.origin;
  const template = String(c.template ?? "national_workshop");
  // A block with no office is an empty line the form left behind; the server
  // drops those too, but a preview renders before anything is ever saved.
  const signatures = (c.signatures ?? []).filter((x) => x && (x.title ?? "").trim() !== "");
  const cols = Math.min(signatures.length, 4);
  const copy = COPY[template] ?? COPY.national_workshop;
  // The seal keeps to the design's own position until a third block takes the
  // middle away from it.
  const sealLeft = SEAL_LEFT[template] === true || cols >= 3;
  const inked = signatures.some((x) => (x.signature_url ?? "").trim() !== "");

  const title = headlineTitle(c);
  // The headline is the one block that can be any length. Three sizes rather
  // than auto-shrinking script: a measured fit would have to run after layout,
  // and the print window prints before it could.
  const titleClass = title.length > 58 ? "title xlong" : title.length > 34 ? "title long" : "title";

  // The dotted rule belongs to the workshop sheet; the rest rule solid.
  const nameRule = template === "national_workshop" ? "dotted" : "solid";

  // Autoprint waits for the webfonts. Printing on `load` alone prints the
  // fallback faces — the script heading would come out as plain serif, which
  // is the single most visible thing on the sheet.
  const printScript = opts?.autoPrint === false ? "" : `
  <script>
    (function () {
      var fired = false;
      var go = function () { if (fired) return; fired = true; window.focus(); window.print(); };
      // Cap the wait: offline, Google Fonts never settles and the member is
      // left looking at a page with no print dialog and no explanation.
      setTimeout(go, 2500);
      if (document.fonts && document.fonts.ready) { document.fonts.ready.then(function () { setTimeout(go, 250); }); }
      else { window.addEventListener('load', go); }
    })();
  </script>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Certificate${c.recipient_name ? ` — ${esc(c.recipient_name)}` : ""}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700&family=Pinyon+Script&family=Poppins:wght@400;500;600;700;800&family=UnifrakturMaguntia&display=swap">
<style>${STYLES}</style>
</head>
<body>
<div class="sheet tpl-${esc(template)}">
  ${chrome(template)}
  <div class="watermark"><img src="${esc(origin)}/niqs-logo.png" alt="" onerror="this.closest('.watermark').remove()"></div>

  <div class="content">
    <img class="lockup" src="${esc(origin)}/niqs-logo.png" alt="Nigerian Institute of Quantity Surveyors" onerror="this.remove()">
    ${heading(template)}

    <div class="mark">
      <div class="certify">${esc(copy.certify)}</div>

      <div class="name-line ${nameRule}">
        <span class="name">${esc(c.recipient_name)}</span>
      </div>

      <div class="lead">${copy.lead.map(esc).join("<br>")}</div>

      <div class="${titleClass}">${esc(title)}</div>
      ${c.event_theme ? `<div class="theme">&ldquo;${esc(c.event_theme)}&rdquo;</div>` : ""}
    </div>

    <div class="footer">
      <div class="foot${cols >= 3 ? ` cols-${cols}` : ""}${sealLeft ? " seal-left" : ""}${inked ? " inked" : ""}">
        ${sealSvg()}
        ${signatures.map((s) => {
          const name = (s.name ?? "").trim();
          const ink  = (s.signature_url ?? "").trim();
          return `<div class="sig${name ? "" : " no-name"}">`
            // Rendered on every block once any block is inked, so the rules
            // stay on one line whether or not this particular one is signed.
            + (inked
                ? `<div class="sig-ink">${ink ? `<img src="${esc(ink)}" alt="" onerror="this.remove()">` : ""}</div>`
                : "")
            + `<div class="sig-body">`
            + (name ? `<div class="sig-name">${esc(name)}</div>` : "")
            + `<div class="sig-title">${esc(s.title)}</div>`
            + `</div></div>`;
        }).join("")}
      </div>
      ${c.certificate_no ? `<div class="serial">${esc(c.certificate_no)}</div>` : ""}
    </div>
  </div>
</div>${printScript}
</body>
</html>`;
}

/** Opens the certificate in a print window. */
export function printCertificate(c: CertificateData) {
  const w = window.open("", "_blank", "width=1180,height=860");
  if (!w) return;
  w.document.write(certificateDocument(c));
  w.document.close();
}

// ── Preview ──────────────────────────────────────────────────────────────────

/**
 * The same document, scaled into the page.
 *
 * An iframe rather than a React re-implementation, so the preview cannot drift
 * from the print. The sheet is laid out at its true A4 landscape size and
 * scaled down by measurement — `zoom` would have been shorter and is not a
 * thing outside Chromium.
 */
export function CertificatePreview({ data, className = "" }: { data: CertificateData; className?: string }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  // A4 landscape at 96dpi — the pixel size the iframe's own mm layout comes to.
  const W = 1123;
  const H = 794;

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const fit = () => setScale(el.clientWidth / W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={boxRef} className={`relative w-full overflow-hidden ${className}`} style={{ height: scale ? H * scale : 0 }}>
      <iframe
        title="Certificate preview"
        // autoPrint off: a preview that opened the print dialog by itself would
        // be unusable on the event form, where the admin is comparing designs.
        srcDoc={certificateDocument(data, { autoPrint: false })}
        sandbox="allow-same-origin"
        scrolling="no"
        style={{
          width: W,
          height: H,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ── Sample ───────────────────────────────────────────────────────────────────

/**
 * Stand-in content for previewing a design before anything has been issued —
 * the event form, where the admin is choosing a format and there is no
 * recipient yet. Takes whatever of the real event is already typed in.
 */
export function sampleCertificate(
  template: string,
  signatures: CertificateSignature[],
  event?: { title?: string; theme?: string; year?: string | number; location?: string; start_date?: string; end_date?: string; cpd_points?: string | number },
): CertificateData {
  const year = event?.year || new Date().getFullYear();

  return {
    template,
    signatures,
    certificate_no: `NIQS/CERT/${year}/00000`,
    recipient_name: "Qs. Adaeze N. Okonkwo",
    event_title: event?.title?.trim() || "National Workshop",
    event_theme: event?.theme?.trim() || undefined,
    event_year: year,
    event_location: event?.location?.trim() || undefined,
    event_start_date: event?.start_date || undefined,
    event_end_date: event?.end_date || undefined,
    cpd_points: event?.cpd_points || undefined,
  };
}
